import { BatchActionBar, SelectModeButton } from "@/components/BatchActionBar";
import {
	type EntityFilterSchema,
	FilterBar,
	mergeFilterGroups,
	useFilters,
} from "@/components/filters";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { KanbanBoard } from "@/components/work-items/KanbanBoard";
import {
	Filter,
	FilterGroup,
	FilterGroup_Logic,
} from "@/gen/omnios/v1/common_pb";
import { WorkItemService } from "@/gen/omnios/v1/work_item_connect";
import { WorkItemKind } from "@/gen/omnios/v1/work_item_pb";
import type { ConfigOption } from "@/gen/omnios/v1/work_item_pb";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { setEntityDragData as _setEntityDragData } from "@/lib/entity-context";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { cn } from "@/lib/utils";
import { Value } from "@bufbuild/protobuf";
import { createClient } from "@connectrpc/connect";

import type { ColDef, SelectionChangedEvent } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useGridSelection } from "@/hooks/use-grid-selection";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import {
	ChevronDown,
	ClipboardList,
	Columns3,
	LayoutList,
	ListTodo,
	Plus,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkItemsListShortcuts } from "@/hooks/use-work-items-shortcuts";

export const Route = createFileRoute("/work-items/")({
	component: WorkItemsPage,
});

const client = createClient(WorkItemService, transport);

const KIND_MAP: Record<string, WorkItemKind> = {
	case: WorkItemKind.CASE,
	task: WorkItemKind.TASK,
	sub_task: WorkItemKind.SUB_TASK,
};

function useKindLabel() {
	const { t } = useTranslation("work-items");
	return useCallback(
		(kind: WorkItemKind) => {
			switch (kind) {
				case WorkItemKind.CASE:
					return t("kind_case");
				case WorkItemKind.TASK:
					return t("kind_task");
				case WorkItemKind.SUB_TASK:
					return t("kind_sub_task");
				default:
					return t("kind_dash");
			}
		},
		[t],
	);
}

function kindColor(kind: WorkItemKind): string {
	switch (kind) {
		case WorkItemKind.CASE:
			return "hsl(var(--chart-1))";
		case WorkItemKind.TASK:
			return "hsl(var(--chart-2))";
		case WorkItemKind.SUB_TASK:
			return "hsl(var(--chart-4))";
		default:
			return "hsl(var(--muted-foreground))";
	}
}

// ── Main page ──

function WorkItemsPage() {
	const { t } = useTranslation("work-items");
	const kindLabel = useKindLabel();
	useBreadcrumbs([{ label: t("breadcrumb") }]);

	const navigate = useNavigate();
	const qc = useQueryClient();
	const { onGridReady, onStateChanged } = useGridLayout("work-items");
	const { selectedRows, onSelectionChanged: onGridSelectionChanged } =
		useGridSelection();

	const searchParams = useSearch({
		from: "/work-items",
	});
	const kindParam = searchParams.kind;
	const typeParam = (searchParams as Record<string, unknown>).type as
		| string
		| undefined;
	const viewParam =
		(searchParams as Record<string, unknown>).view === "board"
			? "board"
			: "table";

	const activeKind: WorkItemKind =
		(kindParam ? KIND_MAP[kindParam] : undefined) ??
		WorkItemKind.WORK_ITEM_KIND_UNSPECIFIED;

	const [selectMode, setSelectMode] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [selectAll, setSelectAll] = useState(false);

	// Fetch work item types for kind filter
	const { data: workItemTypes } = useQuery({
		queryKey: ["config-options", "work_item_type"],
		queryFn: () => client.listConfigOptions({ domain: "work_item_type" }),
	});

	// Build type config IDs for case/task kind filter
	const caseTypeIds = (workItemTypes?.options ?? [])
		.filter((t) => {
			const attrs = t.attrs?.toJson() as Record<string, unknown> | undefined;
			return attrs?.kind === "case";
		})
		.map((t) => t.id);

	const taskTypeIds = (workItemTypes?.options ?? [])
		.filter((t) => {
			const attrs = t.attrs?.toJson() as Record<string, unknown> | undefined;
			return attrs?.kind === "task";
		})
		.map((t) => t.id);

	// Determine filter for type
	let typeConfigIds: string[] = [];
	if (typeParam) {
		typeConfigIds = [typeParam];
	} else if (kindParam === "case") {
		typeConfigIds = caseTypeIds;
	} else if (kindParam === "task") {
		typeConfigIds = taskTypeIds;
	}

	// Build status options from config
	const { data: statusOptions } = useQuery({
		queryKey: ["config-options", "work_item_status"],
		queryFn: () => client.listConfigOptions({ domain: "work_item_status" }),
	});

	const { data: priorityOptions } = useQuery({
		queryKey: ["config-options", "priority"],
		queryFn: () => client.listConfigOptions({ domain: "priority" }),
	});

	const { data: transitionsData } = useQuery({
		queryKey: ["status-transitions", "work_item_status"],
		queryFn: () =>
			client.listStatusTransitions({ domain: "work_item_status" }),
	});

	// Filter schema for work items
	const filterSchema: EntityFilterSchema = {
		entityType: "work_item",
		fields: [
			{
				id: "status_config_id",
				label: t("filter_status"),
				type: "config_option" as const,
				backendField: "status_config_id",
				options: (statusOptions?.options ?? []).map((opt) => ({
					value: opt.id,
					label: opt.displayName || opt.name,
					color: opt.color || undefined,
				})),
			},
			{
				id: "priority_config_id",
				label: t("filter_priority"),
				type: "config_option" as const,
				backendField: "priority_config_id",
				options: (priorityOptions?.options ?? []).map((opt) => ({
					value: opt.id,
					label: opt.displayName || opt.name,
					color: opt.color || undefined,
				})),
			},
			{
				id: "title",
				label: t("filter_title"),
				type: "text" as const,
			},
		],
		searchableFields: ["title", "description"],
	};

	// Initialize filters hook
	const filters = useFilters({
		schema: filterSchema,
		syncWithUrl: true,
	});

	// Build type filter group if needed
	const typeFilterGroup =
		typeConfigIds.length > 0
			? new FilterGroup({
					logic: FilterGroup_Logic.OR,
					filters: typeConfigIds.map(
						(id) =>
							new Filter({
								field: "type_config_id",
								op: "eq",
								value: Value.fromJson(id),
							}),
					),
				})
			: undefined;

	// Combine type filter with user filters
	const combinedFilter = mergeFilterGroups([
		typeFilterGroup,
		filters.toProtobuf(),
	]);

	const filterKey = JSON.stringify(filters.state);

	// Board view still needs client-side data
	const { data: boardData } = useQuery({
		queryKey: [
			"work-items-board",
			kindParam ?? "all",
			typeParam ?? "all",
			typeConfigIds.join(","),
			filters.debouncedSearch,
			filterKey,
		],
		queryFn: () =>
			client.listWorkItems({
				kind: activeKind,
				search: filters.debouncedSearch,
				page: { pageSize: 200, pageToken: "" },
				filter: combinedFilter,
			}),
		enabled:
			viewParam === "board" &&
			(kindParam !== "case" || caseTypeIds.length > 0) &&
			(kindParam !== "task" || taskTypeIds.length > 0),
	});
	const boardItems = boardData?.workItems ?? [];

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid({
		fetchRows: async ({ pageSize, pageToken, sort, filter, search }) => {
			const resp = await client.listWorkItems({
				kind: activeKind,
				search: search,
				page: { pageSize, pageToken },
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				filter: filter as typeof combinedFilter,
			});
			return {
				rows: resp.workItems,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		pageSize: 100,
		search: filters.debouncedSearch,
		extraFilter: combinedFilter,
		colToField: {
			createdAt: "created_at",
		},
	});

	const createMut = useMutation({
		mutationFn: (opts: {
			kind: WorkItemKind;
		}) =>
			client.createWorkItem({
				kind: opts.kind,
				title: "",
			}),
		onSuccess: (created) => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["work-items-board"] });
			navigate({
				to: "/work-items/$id",
				params: { id: created.id },
				search: {},
			});
		},
	});

	const deleteMut = useMutation({
		mutationFn: async () => {
			await client.bulkDeleteWorkItems({
				ids: selectAll ? [] : [...selectedIds],
				all: selectAll,
			});
		},
		onSuccess: () => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["work-items-board"] });
			setSelectedIds(new Set());
			setSelectAll(false);
			setSelectMode(false);
		},
	});

	const updateStatusMut = useMutation({
		mutationFn: (opts: { id: string; statusConfigId: string }) =>
			client.updateWorkItem({
				id: opts.id,
				statusConfigId: opts.statusConfigId,
			}),
		onSuccess: () => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["work-items-board"] });
		},
	});

	// Column defs
	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "number",
				headerName: t("col_number"),
				width: 70,
				pinned: "left",
				cellClass: "font-mono",
				valueFormatter: (p) => p.data ? (p.value?.toString() ?? "") : "",
			},
			{
				field: "title",
				headerName: t("col_title"),
				flex: 2,
				minWidth: 200,
			},
			{
				field: "kind",
				headerName: t("col_kind"),
				width: 100,
				filter: false,
				cellRenderer: (p: { value: WorkItemKind }) => {
					if (p.value == null) return "\u2014";
					const label = kindLabel(p.value);
					const color = kindColor(p.value);
					return (
						<span style={{ display: "inline-block", padding: "1px 6px", border: `1px solid ${color}33`, borderRadius: 4, fontSize: 11, lineHeight: "18px", color }}>
							{label}
						</span>
					);
				},
			},
			{
				colId: "status",
				headerName: t("col_status"),
				width: 130,
				filter: false,
				valueGetter: (p) => p.data?.statusConfig,
				cellRenderer: (p: { value: ConfigOption | undefined }) => {
					if (!p.value) return "\u2014";
					const color = p.value.color || "hsl(var(--muted-foreground))";
					const label = p.value.displayName || p.value.name;
					return (
						<span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
							<span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: color }} />
							{label}
						</span>
					);
				},
			},
			{
				colId: "priority",
				headerName: t("col_priority"),
				width: 110,
				filter: false,
				valueGetter: (p) => p.data?.priorityConfig,
				cellRenderer: (p: { value: ConfigOption | undefined }) => {
					if (!p.value) return "";
					const color = p.value.color || "hsl(var(--muted-foreground))";
					const label = p.value.displayName || p.value.name;
					return (
						<span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
							<span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: color }} />
							{label}
						</span>
					);
				},
			},
			{
				field: "createdAt",
				headerName: t("col_created"),
				width: 110,
				filter: "agDateColumnFilter",
				cellClass: "font-tabular",
				valueFormatter: (p) => {
					const ts = p.data?.createdAt;
					return ts
						? new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU")
						: "\u2014";
				},
			},
		],
		[t, kindLabel],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onSelectionChanged = useCallback(
		(e: SelectionChangedEvent) => {
			onGridSelectionChanged(e);
			const rows = e.api.getSelectedRows();
			setSelectedIds(new Set(rows.map((r: any) => r.id)));
		},
		[onGridSelectionChanged],
	);

	const onRowClicked = useCallback(
		(e: { data: any }) => {
			if (e.data) {
				navigate({
					to: "/work-items/$id",
					params: { id: e.data.id },
					search: {},
				});
			}
		},
		[navigate],
	);

	// Status bar
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${totalRows} ${t("entity_label", { defaultValue: "\u0437\u0430\u0434\u0430\u0447" })}`,
				align: "left" as const,
			},
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	useWorkItemsListShortcuts({
		onSwitchView: useCallback(
			(view: "table" | "board") =>
				navigate({
					search: ((prev: Record<string, unknown>) => ({ ...prev, view })) as never,
				}),
			[navigate],
		),
		onRefresh: useCallback(
			() => refreshGrid(),
			[refreshGrid],
		),
		onCreate: useCallback(
			() => createMut.mutate({ kind: WorkItemKind.TASK }),
			[createMut],
		),
	});

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center gap-2 px-4 py-2">
				<FilterBar
					filters={filters}
					config={{
						searchPlaceholder: t("search_placeholder"),
						compact: true,
						autoFocus: true,
					}}
					className="flex-1"
				/>
				<div className="flex items-center gap-2 shrink-0">
					{/* View switcher */}
					<div className="flex items-center rounded-md border border-border">
						<button
							type="button"
							onClick={() =>
								navigate({
									to: "/work-items",
									search: { ...searchParams, view: "table" } as never,
								})
							}
							className={cn(
								"inline-flex items-center justify-center h-7 w-7 rounded-l-md transition-colors",
								viewParam === "table"
									? "bg-accent text-accent-foreground"
									: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
							)}
							title={t("view_table")}
						>
							<LayoutList className="h-3.5 w-3.5" />
						</button>
						<button
							type="button"
							onClick={() =>
								navigate({
									to: "/work-items",
									search: { ...searchParams, view: "board" } as never,
								})
							}
							className={cn(
								"inline-flex items-center justify-center h-7 w-7 rounded-r-md transition-colors",
								viewParam === "board"
									? "bg-accent text-accent-foreground"
									: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
							)}
							title={t("view_board")}
						>
							<Columns3 className="h-3.5 w-3.5" />
						</button>
					</div>

					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() => refreshGrid()}
					>
						{t("refresh", { defaultValue: "Обновить" })}
					</Button>
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() =>
							gridRef.current?.api?.exportDataAsCsv({
								fileName: "work-items.csv",
							})
						}
					>
						{t("common:export", { defaultValue: "Экспорт" })}
					</Button>
					<SelectModeButton
						selectMode={selectMode}
						onToggle={() => {
							setSelectMode((v) => !v);
							setSelectedIds(new Set());
							setSelectAll(false);
						}}
						disabled={totalRows === 0}
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button size="sm" data-onboarding="create-item">
								<Plus className="h-4 w-4 mr-1" />
								{t("common:create")}
								<ChevronDown className="h-3.5 w-3.5 ml-1" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onSelect={() =>
									createMut.mutate({
										kind: WorkItemKind.CASE,
									})
								}
							>
								<ClipboardList className="h-4 w-4 mr-2" />
								{t("create_case")}
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={() =>
									createMut.mutate({
										kind: WorkItemKind.TASK,
									})
								}
							>
								<ListTodo className="h-4 w-4 mr-2" />
								{t("create_task")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Grid / Board */}
			{viewParam === "board" ? (
				<KanbanBoard
					items={boardItems}
					statusOptions={statusOptions?.options ?? []}
					transitions={transitionsData?.transitions}
					onStatusChange={(itemId, newStatusConfigId) =>
						updateStatusMut.mutate({
							id: itemId,
							statusConfigId: newStatusConfigId,
						})
					}
					onItemClick={(id) =>
						navigate({
							to: "/work-items/$id",
							params: { id },
							search: {},
						})
					}
				/>
			) : (
				<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
					<AgGridReact
						ref={gridRef}
						{...gridProps}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						rowSelection="multiple"
						onSelectionChanged={onSelectionChanged}
						onRowClicked={onRowClicked}
						onGridReady={onGridReady}
						onSortChanged={() => { onStateChanged(); refreshGrid(); }}
						onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						suppressRowClickSelection
						rowClass="cursor-pointer"
						animateRows={false}
						rowHeight={28}
						headerHeight={32}
						getRowId={(p) => p.data?.id}
						overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_title", { defaultValue: "\u041D\u0435\u0442 \u0437\u0430\u0434\u0430\u0447" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_description", { defaultValue: "\u0421\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u043F\u0435\u0440\u0432\u0443\u044E \u0437\u0430\u0434\u0430\u0447\u0443" })}</p></div>`}
						overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
					/>
				</div>
			)}

			<BatchActionBar
				selectMode={selectMode}
				count={selectAll ? totalRows : selectedIds.size}
				total={totalRows}
				selectAll={selectAll}
				onSelectAll={() => {
					setSelectAll(true);
					const rows: string[] = [];
					gridRef.current?.api?.forEachNode((node) => {
						if (node.data?.id) rows.push(node.data.id);
					});
					setSelectedIds(new Set(rows));
				}}
				onClear={() => {
					setSelectedIds(new Set());
					setSelectAll(false);
					setSelectMode(false);
				}}
				onDelete={() => deleteMut.mutate()}
				isPending={deleteMut.isPending}
				entityLabel={t("entity_label")}
			/>
		</div>
	);
}
